// Iter 75 — Clip Requests admin triage page. Lives inside the
// authenticated DAM. Lists submissions, lets admin flip status, and
// opens each clip in an AssetDetailModal overlaid on this page.
import { useEffect, useState } from "react";
import Sidebar from "@/components/Sidebar";
import AssetDetailModal from "@/components/AssetDetailModal";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Scissors, Film, Mail, Clock, Play, ChevronDown, ChevronRight, Trash2, Check, Loader2 } from "lucide-react";
import { clipTimecodes } from "@/lib/timecode";

function ClipTcLine({ clip }) {
  const t = clipTimecodes(clip);
  return (
    <>
      In <span className="text-[var(--brand-accent)]">{t.inTc}</span> → Out <span className="text-[var(--brand-accent)]">{t.outTc}</span> · {t.length}
      {t.fpsText && <span className="text-zinc-600"> · {t.fpsText}</span>}
    </>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

const STATUS_PILL = {
  new: "bg-emerald-950/40 text-emerald-400 border-emerald-900/60",
  in_progress: "bg-amber-950/40 text-amber-300 border-amber-900/60",
  reviewed: "bg-zinc-900/80 text-zinc-500 border-zinc-800",
};

export default function ClipRequestsAdmin() {
  // Iter 88 — clip opened in-place: { assetId, inSec, outSec, requester, note, requestId }.
  const [openClip, setOpenClip] = useState(null);
  const [folders, setFolders] = useState([]);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/admin/clip-requests", { params });
      setRows(data);
    } catch (e) {
      toast.error("Could not load clip requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { api.get("/folders").then((r) => setFolders(r.data)); }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status]);

  const setRowStatus = async (id, next) => {
    try {
      const { data } = await api.patch(`/admin/clip-requests/${id}`, { status: next });
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      window.dispatchEvent(new CustomEvent("wd:clip-requests-updated"));
      toast.success(`Marked ${next.replace("_", " ")}`);
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this clip request? The submitter will not be notified.")) return;
    try {
      await api.delete(`/admin/clip-requests/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      window.dispatchEvent(new CustomEvent("wd:clip-requests-updated"));
    } catch { toast.error("Could not delete"); }
  };

  // Escape closes the in-place clip window (ignored while typing in a field).
  useEffect(() => {
    if (!openClip) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      setOpenClip(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openClip]);

  // Iter 90 — openClip = { req, index }; the modal is keyed per clip so each
  // one loads fresh with its own banner, highlight and in-point.
  const jumpToClip = (clip, req) => {
    if (!clip.asset_id) { toast.error("Clip is missing its video — cannot open"); return; }
    setOpenClip({ req, index: Math.max(0, (req?.clips || []).indexOf(clip)) });
  };

  const clipCtx = (() => {
    if (!openClip) return null;
    const { req, index } = openClip;
    const clips = req?.clips || [];
    const c = clips[index];
    if (!c) return null;
    const go = (i) => setOpenClip({ req, index: i });
    return {
      assetId: c.asset_id,
      inSec: Number(c.in_sec) || 0,
      outSec: Number(c.out_sec) || 0,
      frameRate: c.frame_rate,
      frameRateAssumed: c.frame_rate_assumed,
      inFrame: c.in_frame,
      outFrame: c.out_frame,
      requester: req?.requester_name || "",
      note: c.note || "",
      requestId: req?.id || "",
      nav: clips.length > 1 ? {
        index, total: clips.length,
        onPrev: () => go(index - 1), onNext: () => go(index + 1), onStart: () => go(0),
      } : undefined,
    };
  })();

  return (
    <div className="flex min-h-screen grain-overlay">
      <Toaster theme="dark" position="bottom-right" richColors />
      <Sidebar folders={folders} onRefresh={() => api.get("/folders").then((r) => setFolders(r.data))} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar view={view} setView={setView} onSearch={setQ} searchValue={q} onUpload={() => {}} canUpload={false} />
        <main className="flex-1 px-4 sm:px-6 md:px-10 lg:px-12 py-6 md:py-8 pb-20">
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Public submissions</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
              <Scissors className="w-7 h-7 text-[var(--brand-accent)]" />
              Clip Requests
            </h1>
            <p className="text-sm text-zinc-500 mt-1">{rows.length} {rows.length === 1 ? "submission" : "submissions"}</p>
          </div>

          <div className="mb-5 flex items-center gap-2 flex-wrap">
            {[
              { k: "", label: "All" },
              { k: "new", label: "New" },
              { k: "in_progress", label: "In progress" },
              { k: "reviewed", label: "Reviewed" },
            ].map((opt) => (
              <button
                key={opt.k}
                data-testid={`clip-filter-${opt.k || "all"}`}
                onClick={() => setStatus(opt.k)}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${status === opt.k ? "bg-[var(--brand-accent)] text-black border-transparent font-semibold" : "border-zinc-800 text-zinc-400 hover:border-[var(--brand-accent)]/60 hover:text-zinc-100"}`}
              >
                {opt.label}
              </button>
            ))}
            <form onSubmit={(e) => { e.preventDefault(); refresh(); }} className="ml-auto">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name/email…"
                data-testid="clip-search"
                className="bg-zinc-950/60 border border-zinc-800 rounded-full px-4 py-1.5 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60"
              />
            </form>
          </div>

          {loading ? (
            <div className="text-center text-xs text-zinc-500 py-20"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center text-xs text-zinc-500 py-20 border border-dashed border-zinc-900 rounded-xl">No clip requests {status ? `with status "${status}"` : "yet"}.</div>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => {
                const isOpen = !!expanded[r.id];
                return (
                  <div key={r.id} data-testid={`clip-row-${r.id}`} className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpanded((prev) => ({ ...prev, [r.id]: !prev[r.id] }))}
                      className="w-full flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02] transition text-left"
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-zinc-100 truncate">{r.requester_name}</div>
                        <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5">
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.requester_email}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{relTime(r.created_at)}</span>
                          <span className="flex items-center gap-1"><Film className="w-3 h-3" />{r.clips.length} clip{r.clips.length === 1 ? "" : "s"}</span>
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${STATUS_PILL[r.status] || STATUS_PILL.new}`}>
                        {r.status.replace("_", " ")}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="border-t border-zinc-900 bg-black/30">
                        <div className="divide-y divide-zinc-900">
                          {r.clips.map((c, i) => (
                            <div key={i} data-testid={`clip-detail-${r.id}-${i}`} className="px-5 py-3 flex items-start gap-3">
                              <Film className="w-3.5 h-3.5 text-zinc-500 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-zinc-100 font-medium truncate">{c.asset_title || "(untitled)"}</div>
                                <div className="text-[11px] text-zinc-500 tabular-nums mt-0.5" data-testid={`clip-tc-${r.id}-${i}`}>
                                  <ClipTcLine clip={c} />
                                </div>
                                {c.note && <div className="text-[11px] text-zinc-400 mt-1 italic">“{c.note}”</div>}
                              </div>
                              <button
                                type="button"
                                data-testid={`clip-jump-${r.id}-${i}`}
                                onClick={() => jumpToClip(c, r)}
                                className="shrink-0 flex items-center gap-1.5 bg-black/40 hover:bg-[var(--brand-accent)] hover:text-black border border-zinc-800 hover:border-[var(--brand-accent)] rounded-full px-3 py-1 text-[11px] transition"
                                title="Open the asset in the DAM at the In timecode"
                              >
                                <Play className="w-3 h-3" /> Jump to clip
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="px-5 py-3 border-t border-zinc-900 flex items-center gap-2 flex-wrap">
                          {["new", "in_progress", "reviewed"].map((opt) => (
                            <button
                              key={opt}
                              data-testid={`clip-set-${opt}-${r.id}`}
                              onClick={() => setRowStatus(r.id, opt)}
                              disabled={r.status === opt}
                              className={`text-[11px] rounded-full px-3 py-1 border transition ${r.status === opt ? "bg-[var(--brand-accent)] text-black border-transparent font-semibold" : "border-zinc-800 text-zinc-400 hover:border-[var(--brand-accent)]/60 hover:text-zinc-100"}`}
                            >
                              {opt === "new" && <Check className="w-3 h-3 inline mr-1" />}
                              {opt.replace("_", " ")}
                            </button>
                          ))}
                          <button
                            onClick={() => remove(r.id)}
                            data-testid={`clip-delete-${r.id}`}
                            className="ml-auto text-[11px] text-zinc-500 hover:text-red-400 transition flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
      {clipCtx && (
        <AssetDetailModal
          key={`${clipCtx.requestId}-${openClip.index}`}
          assetId={clipCtx.assetId}
          initialSeekSec={clipCtx.inSec > 0 ? clipCtx.inSec : undefined}
          clipRequest={clipCtx}
          onClose={() => setOpenClip(null)}
          onChange={() => {}}
        />
      )}
    </div>
  );
}
